import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback, useState } from "react";

import {
  AudienceType,
  CreateSuppressionsFromAudiencesMutation,
  CreateSuppressionsFromAudiencesMutationVariables,
  ListAudiencesForSubaccountQuery,
  ListAudiencesForSubaccountQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import TooltipButton from "~frontend/components/TooltipButton";
import { LIST_AUDIENCES_FOR_SUBACCOUNT } from "~frontend/graphql/audience";
import { CREATE_SUPPRESSIONS_FROM_AUDIENCES } from "~frontend/graphql/campaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

import { isSendInProgress } from "./sendInProgress";
import { isSuppressionJobRunning } from "./suppressionJobs";

export function SuppressAudiences({
  campaign,
}: {
  campaign: CampaignWithSuppressionData;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const errorMessage = useErrorMessageFormatter();
  const confirm = useConfirm();
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<string[]>([]);

  const audiencesQuery = useQuery<
    ListAudiencesForSubaccountQuery,
    ListAudiencesForSubaccountQueryVariables
  >(LIST_AUDIENCES_FOR_SUBACCOUNT, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const [startSuppressionJob, { loading: startingSuppressionJob }] =
    useMutation<
      CreateSuppressionsFromAudiencesMutation,
      CreateSuppressionsFromAudiencesMutationVariables
    >(CREATE_SUPPRESSIONS_FROM_AUDIENCES, {
      onErrorSnackbarMessage: "Error starting suppression",
    });

  const handleStartSuppressionJob = useCallback(async () => {
    if (selectedAudienceIds.length === 0) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Suppress Audience Contacts",
      description:
        "This will suppress all contacts in the selected audiences from this campaign.",
    });
    if (!confirmed) {
      return;
    }

    await startSuppressionJob({
      variables: {
        input: { campaignId: campaign.id, audienceIds: selectedAudienceIds },
      },
    });

    setSelectedAudienceIds([]);
  }, [selectedAudienceIds, confirm, startSuppressionJob, campaign]);

  const handleAudienceSelectionChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value;
      setSelectedAudienceIds(
        typeof value === "string" ? value.split(",") : value
      );
    },
    []
  );

  const suppressedAudienceIds = new Set(
    campaign.audienceSuppressions.map(s => s.audience.id)
  );

  const customAudiences =
    audiencesQuery.data?.listAudiencesForSubaccount.filter(
      audience =>
        audience.type === AudienceType.Custom &&
        !suppressedAudienceIds.has(audience.id)
    ) ?? [];

  if (customAudiences.length === 0) {
    return <></>;
  }

  if (audiencesQuery.error) {
    return (
      <Alert severity="error">
        Error fetching audiences: {errorMessage(audiencesQuery.error)}
      </Alert>
    );
  }

  const sendInProgress = isSendInProgress(campaign);

  return (
    <Box sx={{ backgroundColor: palette.lightestGray, padding: "24px 36px" }}>
      <FlexBox justifyContent="space-between" direction="column">
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1Bold">Audiences</Typography>
          <FormControl fullWidth>
            <Select
              multiple
              label="Audience"
              value={selectedAudienceIds}
              onChange={handleAudienceSelectionChange}
              displayEmpty
              renderValue={selected => {
                if (selected.length === 0) {
                  return "Select audiences...";
                }
                const selectedNames = customAudiences
                  .filter(audience => selected.includes(audience.id))
                  .map(audience => audience.name);
                return selectedNames.join(", ");
              }}
              disabled={sendInProgress || isSuppressionJobRunning(campaign)}
            >
              {customAudiences.map(audience => (
                <MenuItem key={audience.id} value={audience.id}>
                  {audience.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FlexBox justifyContent="flex-end" sx={{ pt: 1, width: "100%" }}>
          <Box>
            <TooltipButton
              variant="contained"
              onClick={handleStartSuppressionJob}
              disabled={
                selectedAudienceIds.length === 0 ||
                sendInProgress ||
                startingSuppressionJob ||
                isSuppressionJobRunning(campaign)
              }
              tooltipText={
                sendInProgress
                  ? "Cannot suppress contacts while sending is in progress"
                  : selectedAudienceIds.length === 0
                    ? "Please select at least one audience"
                    : undefined
              }
            >
              {`Suppress All Contacts in ${plural(selectedAudienceIds.length, "Audience", "Audiences")}`}
            </TooltipButton>
          </Box>
        </FlexBox>
      </FlexBox>
    </Box>
  );
}
